/* eslint-disable @typescript-eslint/no-unused-vars */
import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { BtrPassportCheckInInfoModel } from '@app/core/models/btr-passport-check-in-info.model';
import {
  EventsService,
  SnackbarService,
  StorageService,
} from '@app/core/services';
import { BtrPassportApiService } from '@app/shared/services/api';

const TSTATE_KEY =
  makeStateKey<BtrPassportCheckInInfoModel>('check-in-resolver');

@Injectable()
export class CheckInResolver implements Resolve<BtrPassportCheckInInfoModel> {
  private readonly isServer: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly btrPassportApiService: BtrPassportApiService,
    private readonly transferState: TransferState,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
    private readonly sessionStorageService: StorageService,
    private readonly eventsService: EventsService,
  ) {
    this.isServer = isPlatformServer(this.platformId);
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<BtrPassportCheckInInfoModel>
    | Promise<BtrPassportCheckInInfoModel>
    | BtrPassportCheckInInfoModel {
    const token = route.paramMap.get('token');
    return this.isServer
      ? this.serverResolve(token)
      : this.browserResolve(token);
  }

  private browserResolve(
    token: string,
  ): Observable<BtrPassportCheckInInfoModel> | BtrPassportCheckInInfoModel {
    const tmp = this.transferState.get<
      Pick<BtrPassportCheckInInfoModel, keyof BtrPassportCheckInInfoModel>
    >(TSTATE_KEY, null);

    // TODO: delete "&& !this.sessionStorageService.USER"
    //  after finding reason why API from server doesn't return USER passports
    this.eventsService.setIsFirstLoading(false);
    this.eventsService.setIsLoading(false);
    if (tmp != null && !this.sessionStorageService.USER) {
      this.transferState.remove<BtrPassportCheckInInfoModel>(TSTATE_KEY);
      return BtrPassportCheckInInfoModel.copyObj(tmp);
    }
    return this.btrPassportApiService.getPassportCheckInInfo(token).pipe(
      catchError(err => {
        this.snackbarService.showError('Page not found', 1000);
        this.router.navigate(['page-not-found']);
        console.error(err);
        return of(null);
      }),
    );
  }

  private serverResolve(
    token: string,
  ): Observable<BtrPassportCheckInInfoModel> {
    this.eventsService.setIsFirstLoading(false);
    this.eventsService.setIsLoading(false);
    return this.btrPassportApiService.getPassportCheckInInfo(token).pipe(
      tap(data => {
        this.transferState.set<BtrPassportCheckInInfoModel>(TSTATE_KEY, data);
      }),
      catchError(err => {
        this.router.navigate(['page-not-found']);
        console.error(err);
        return of(null);
      }),
    );
  }
}
